import { months, prices, weekdays } from "./config";
import { parsePhoneNumberFromString } from 'libphonenumber-js';

export const getRussianOrdinal = (n) => {
    n = Number(n);
    if (!Number.isInteger(n) || n <= 0) return String(n);

    const lastDigit = n % 10;
    const lastTwoDigits = n % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
        return `${n}-ый`;
    }

    switch (lastDigit) {
        case 2:
        case 6:
        case 7:
        case 8:
            return `${n}-ой`;
        case 3:
            return `${n}-ий`;
        default:
            return `${n}-ый`;
    }
}

export const formatTime = (timeStr) => {
    let [hours, minutes] = timeStr.split(':').map(Number);
    hours = hours.toString().padStart(2, '0');
    minutes = minutes.toString().padStart(2, '0');
    return `${hours}:${minutes}`;
}

export const calcSalePrice = (price, count, sale) => {
    const numericPrice = Number(price);
    const numericCount = Number(count);
    
    if (!sale || sale === '0' || sale === '0%') {
        return numericPrice * numericCount;
    }

    if (sale.includes('%')) {
        const discountPercent = Number(sale.replace('%', ''));
        return Math.round((numericPrice * numericCount) * (100 - discountPercent) / 100);
    }

    return (numericPrice * numericCount) - Number(sale);
};

export const processSmeta = (smeta) => {
    if (!smeta) return null;
    
    const fullPrice = Number(smeta.Price) * Number(smeta.Count);
    const discountedPrice = calcSalePrice(smeta.Price, smeta.Count, smeta.Sale);
    const saleDisplay = smeta.Sale === '0' || smeta.Sale === '0%' ? '—' : smeta.Sale;

    return {
        ...smeta,
        fullPrice,
        discountedPrice,
        saleDisplay
    };
};

export const processOrder = (order) => {
    const processedSmets = order.Smets.map(processSmeta).filter(Boolean);
    
    const smetaSumma = processedSmets.reduce((sum, smeta) => sum + smeta.discountedPrice, 0);
    const smetaSummaAll = processedSmets.reduce((sum, smeta) => sum + smeta.fullPrice, 0);
    const smetaSalePerc = processedSmets.find(smeta => smeta.Type === 0)?.saleDisplay || '—';
    const smetaIsDopUslugi = processedSmets.find(smeta => smeta.Category === 2) ? true : false;

    return {
        ...order,
        Smets: processedSmets,
        smetaSumma,
        smetaSummaAll,
        smetaSalePerc,
        smetaIsDopUslugi
    };
};

export const hoursToText = (hours) => {
    const wholeHours = Math.floor(hours);
    const decimalPart = hours - wholeHours;
    const minutes = Math.round(decimalPart * 60);

    const hourWord = getHourWord(wholeHours);
    const minuteWord = getMinuteWord(minutes);

    let result = `${wholeHours} ${hourWord}`;
    if (minutes > 0) {
        result += ` ${minutes} ${minuteWord}`;
    }

    return result;
}

export const getHourWord = (hours) => {
    const lastDigit = hours % 10;
    const lastTwoDigits = hours % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
        return 'часов';
    }

    switch (lastDigit) {
        case 1:
            return 'час';
        case 2:
        case 3:
        case 4:
            return 'часа';
        default:
            return 'часов';
    }
}

export const getMinuteWord = (minutes) => {
    const lastDigit = minutes % 10;
    const lastTwoDigits = minutes % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
        return 'минут';
    }

    switch (lastDigit) {
        case 1:
            return 'минута';
        case 2:
        case 3:
        case 4:
            return 'минуты';
        default:
            return 'минут';
    }
}

export const formatMoney = (number, decimalSeparator = ',', thousandSeparator = '.') => {
    if (typeof number !== 'number' || isNaN(number)) {
        return '0';
    }
    
    const parts = number.toFixed(2).split('.');
    let integerPart = parts[0];
    
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
    
    return integerPart;
}

export const formatTimestampRange = (startTs, endTs) => {
    const start = new Date(startTs);
    const end = new Date(endTs);

    const daysOfWeek = weekdays;

    const day = start.getDate();
    const month = months[start.getMonth()];
    const year = start.getFullYear();
    const weekday = daysOfWeek[start.getDay()];

    const pad = n => n.toString().padStart(2, '0');
    const startTime = `${pad(start.getHours())}:${pad(start.getMinutes())}`;
    const endTime = `${pad(end.getHours())}:${pad(end.getMinutes())}`;

    const diffMs = endTs - startTs;
    const diffMin = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMin / 60);
    const minutes = diffMin % 60;

    const duration = (hours ? `${hours} ${getHourWord(hours)}` : '') + (minutes ? ` ${minutes} ${getMinuteWord(minutes)}` : '');

    return `${day} ${month} ${year}, ${weekday}, ${startTime}-${endTime}, ${duration}`;
}

export const formatTimeDifference = (timestampToCheck, referenceTimestamp) => {
    if (timestampToCheck < referenceTimestamp) {
        // Разница, если timestampToCheck меньше referenceTimestamp
        const diff = referenceTimestamp - timestampToCheck;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        return `-${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    } else {
        // Если timestampToCheck >= referenceTimestamp, выводим время в формате HH:MM
        const date = new Date(timestampToCheck);
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    }
}

export const formatTimeIfNextDay = (timestamp1, timestamp2) => {
    // Создаем объекты Date из timestamp
    const date1 = new Date(timestamp1);
    const date2 = new Date(timestamp2);

    // Получаем начало дня (00:00:00) для каждого timestamp
    const startOfDay1 = new Date(date1);
    startOfDay1.setHours(0, 0, 0, 0);

    const startOfDay2 = new Date(date2);
    startOfDay2.setHours(0, 0, 0, 0);

    // Проверяем, находится ли timestamp1 на следующий день после timestamp2
    if (startOfDay1.getTime() > startOfDay2.getTime()) {
        // Форматируем время с +24 часами
        const hours = date1.getHours();
        const minutes = date1.getMinutes().toString().padStart(2, '0');
        return `+24:${hours}:${minutes}`;
    } else {
        // Возвращаем обычное время, если не на следующий день
        const hours = date1.getHours().toString().padStart(2, '0');
        const minutes = date1.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    }
}

export const formatTimeFromTimestamp = (date) => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

export const createDate = (dateStr) => {
    const [day, month, year] = dateStr.split('.').map(Number);
    const date = new Date(year, month - 1, day);
    date.setHours(0, 0, 0, 0);
    return date;
};

export const createDateTS = () => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
};

export const formatDurationWords = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 30 === 0 ? minutes % 60 : minutes % 60;
    const parts = [];

    if (hours > 0) parts.push(`${hours} час.`);
    if (mins > 0) parts.push(`${mins} мин.`);

    return parts.join(' ');
}

export const formatDatetimeAutocancel = (targetTimestamp) => {
    const now = new Date();
    const targetDate = new Date(targetTimestamp * 1000);

    const diffMs = targetDate - now;

    if (diffMs <= 0) {
        return "Время внесения предоплаты вышло.";
    }

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;

    const day = targetDate.getDate();
    const month = months[targetDate.getMonth()];

    const formattedHours = String(targetDate.getHours()).padStart(2, '0');
    const formattedMinutes = String(targetDate.getMinutes()).padStart(2, '0');

    return `Необходимо внести предоплату в течение ${hours} час ${minutes} минут, иначе бронь автоматически отменится ${day} ${month} в ${formattedHours}:${formattedMinutes}.`;
}

export const formatDate = (date = new Date()) => {
    const months = [
        'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
        'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
    ];
    
    const weekdays = [
        'воскресенье', 'понедельник', 'вторник', 'среда',
        'четверг', 'пятница', 'суббота'
    ];
    
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const weekday = weekdays[date.getDay()];
    
    return `${day} ${month} ${year}, ${weekday}`;
}

export const formatTimestamp = (timestamp) => {
    const date = new Date(Number(timestamp) * 1000); // если timestamp в секундах
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // месяцы с 0
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}.${month}.${year} ${hours}:${minutes}`;
}

export const isValidFutureDate = (dateStr) => {
    const regex = /^(\d{2})\.(\d{2})\.(\d{4})$/;
    const match = dateStr.match(regex);
    if (!match) return false;

    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    const year = parseInt(match[3], 10);

    const inputDate = new Date(year, month, day);

    if (
        inputDate.getFullYear() !== year ||
        inputDate.getMonth() !== month ||
        inputDate.getDate() !== day
    ) {
        return false;
    }

    inputDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return inputDate >= today;
}

export const handlePhonePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('Text');
    const digits = pasted.replace(/\D/g, '');

    const parsed = parsePhoneNumberFromString(`+${digits}`);
    if (parsed) {
      return parsed.formatInternational();
    } else {
      return `+${digits}`;
    }
};

export const calculateEstimate = (startTimestamp, endTimestamp, peopleCount, filial, hall) => {
    function getRate(minutesOfDay, peopleCount, filial, hall) {
        for (const rate of prices[filial][hall]) {
            if (minutesOfDay >= rate.start && minutesOfDay < rate.end) {
                if (peopleCount >= rate.min_people) {
                    return [rate.price, [rate.startTime, rate.endTime]];
                }
            }
        }
        
        return null;
    }
    
    const intervalMinutes = 30;
    const rateSummary = {};

    while (startTimestamp < endTimestamp) {
        const date = new Date(startTimestamp * 1000);
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const minutesOfDay = hours * 60 + minutes;
        
        const rateData = getRate(minutesOfDay, peopleCount, filial, hall);

        if (rateData !== null) {
            const [rate, rateTime] = rateData;
            const rateKey = `${rate}_${rateTime.join('_')}`;
            
            if (!rateSummary[rateKey]) {
                rateSummary[rateKey] = {
                    rate: parseInt(rate),
                    rateTime: rateTime,
                    minutes: 0
                };
            }

            rateSummary[rateKey].minutes += intervalMinutes;
        }
        
        startTimestamp += intervalMinutes * 60;
    }

    const breakdown = [];

    for (const rateKey of Object.keys(rateSummary)) {
        const { rate, rateTime, minutes } = rateSummary[rateKey];
        const hours = minutes / 60;
        
        breakdown.push({
            rate: rate,
            hours: hours,
            rateTime: rateTime
        });
    }

    return breakdown;
}

export function formatDateRange(startDate, durationMinutes) {
    if (!(startDate instanceof Date)) {
        throw new Error('Первый параметр должен быть объектом Date');
    }
    
    if (isNaN(startDate.getTime())) {
        throw new Error('Передана некорректная дата');
    }
    
    if (typeof durationMinutes !== 'number' || durationMinutes < 0) {
        throw new Error('Продолжительность должна быть неотрицательным числом');
    }
    
    // Форматируем компоненты даты
    const day = String(startDate.getDate()).padStart(2, '0');
    const month = months[startDate.getMonth()];
    const year = startDate.getFullYear();
    const weekDay = weekdays[startDate.getDay()];
    
    // Форматируем время начала
    const formatTime = (date) => {
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
    };
    
    const startTime = formatTime(startDate);
    
    // Вычисляем время окончания
    const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
    const endTime = formatTime(endDate);
    
    // Вычисляем продолжительность в часах и минутах
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    
    // Форматируем продолжительность
    let durationText = '';
    
    if (hours > 0 && minutes > 0) {
        durationText = `${hours} ${getHourWord(hours)} ${minutes} ${getMinuteWord(minutes)}`;
    } else if (hours > 0) {
        durationText = `${hours} ${getHourWord(hours)}`;
    } else {
        durationText = `${minutes} ${getMinuteWord(minutes)}`;
    }
    
    return (
        <>
            <p>{`${day} ${month} ${year}, ${weekDay}`}</p>
            <p>{`${startTime}-${endTime}, ${durationText}`}</p>
        </>
    )
    
    // Вспомогательные функции для склонения
    function getHourWord(hours) {
        if (hours === 1) return 'час';
        if (hours >= 2 && hours <= 4) return 'часа';
        return 'часов';
    }
    
    function getMinuteWord(minutes) {
        if (minutes === 1) return 'минута';
        if (minutes >= 2 && minutes <= 4) return 'минуты';
        return 'минут';
    }
}

export const formatPhoneNumber = (phone) => {
    if (!phone) return '';

    let cleaned = phone.replace(/[^\d+]/g, '');

    if (!cleaned.startsWith('+')) {
        cleaned = '+' + cleaned;
    }

    const parsed = parsePhoneNumberFromString(cleaned);

    if (parsed) {
        return parsed.formatInternational();
    } else {
        return cleaned;
    }
};

export const getGuestsWord = (count) => {
    if (typeof count !== 'number') {
        count = parseInt(count) || 0;
    }
    
    const lastTwoDigits = Math.abs(count) % 100;
    const lastDigit = lastTwoDigits % 10;
    let word;
    
    if (lastTwoDigits >= 5 && lastTwoDigits <= 19) {
        word = 'гостей';
    }
    
    if (lastDigit === 1) {
        word = 'гость';
    }
    
    if (lastDigit >= 2 && lastDigit <= 4) {
        word = 'гостя';
    }
    
    return `${count} ${word}`;
}

export const getHoursFromTS = (timestamp1, timestamp2) => {
    const diffMs = Math.abs(timestamp2*1000 - timestamp1*1000);
    const hours = diffMs / (1000 * 60 * 60);
    
    const roundedHours = Math.round(hours * 10) / 10;
    
    if (roundedHours === 1) {
        return '1 час';
    } else if (roundedHours === Math.floor(roundedHours)) {
        const lastDigit = roundedHours % 10;
        const lastTwoDigits = roundedHours % 100;
        
        if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
            return `${roundedHours} часов`;
        } else if (lastDigit === 1) {
            return `${roundedHours} час`;
        } else if (lastDigit >= 2 && lastDigit <= 4) {
            return `${roundedHours} часа`;
        } else {
            return `${roundedHours} часов`;
        }
    } else {
        return `${roundedHours} часа`;
    }
}

export const isWeekend =(timestamp) => {
    const date = new Date(timestamp);
    const dayOfWeek = date.getDay();
    
    return dayOfWeek === 0 || dayOfWeek === 6;
}